import { getRouteMetadata } from '@/utils/route-metadata';

interface SiteRedirectRefererInfo {
  site_jump_from?: string;
  referrer_url_path?: string;
  referrer_page_name?: string;
  referrer_path_name?: string;
}

/**
 * 延迟执行函数
 */
export const delay = (ms: number): Promise<() => void> => {
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(() => clearTimeout(timer)), ms);
  });
};

/**
 * 安全地执行异步函数
 */
export const safeAsync = async <T>(
  asyncFn: () => Promise<T>,
  errorHandler?: (error: Error) => void,
): Promise<T | null> => {
  try {
    return await asyncFn();
  } catch (error) {
    const err = error instanceof Error ? error : new Error(String(error));
    errorHandler?.(err);
    return null;
  }
};

export const getSiteRedirectEnterPageScene = (referrerInfo?: SiteRedirectRefererInfo) => {
  return (referrerInfo || getSiteRedirectReferrerInfo()).site_jump_from ? '国家站跳转' : '';
};

const getSiteRedirectDecodePageAndExtraCode = () => {
  const info = { page: '', extra: '' };

  try {
    const sp = new URLSearchParams(location.search);
    const shop_sku = sp.get('shop_sku') || '';
    const routeName = getRouteMetadata(location.pathname)?.name;
    const isHome = routeName === 'Home';
    const isGoodsList = routeName === 'GoodsList';
    const isGoodsDetail = routeName === 'GoodsDetail';

    if (isHome) {
      info.page = 'home';
    } else if (isGoodsList) {
      info.page = 'goods-list';
    } else if (isGoodsDetail) {
      info.page = 'goods-detail';
      info.extra = shop_sku;
    }

    return info;
  } catch {
    return info;
  }
};

const getSiteRedirectDecodeFromSiteCode = (fromSiteUrl?: string) => {
  try {
    const url = new URL(fromSiteUrl || location.href);
    const hostPrefix = url.hostname.split('.')[0];
    return hostPrefix === 'www' || hostPrefix === 'm' ? 'us' : hostPrefix;
  } catch {
    return '';
  }
};

/**
 * @desc Decode URL params
 */
export const getSiteRedirectReferrerInfo = () => {
  const referrerInfo: SiteRedirectRefererInfo = {
    site_jump_from: '',
    referrer_url_path: '',
    referrer_page_name: '',
    referrer_path_name: '',
  };

  try {
    const sp = new URLSearchParams(location.search);
    Object.keys(referrerInfo).forEach((key) => {
      const value = sp.get(key);
      if (value) {
        referrerInfo[key as keyof SiteRedirectRefererInfo] = decodeURIComponent(value);
      }
    });
    return referrerInfo;
  } catch {
    return referrerInfo;
  }
};

/**
 * @desc Encode URL params
 */
export const getSiteRedirectEncodeUrlWithReferrer = (originUrl: string) => {
  if (!originUrl) return originUrl;

  try {
    const { page, extra } = getSiteRedirectDecodePageAndExtraCode();
    const siteCode = getSiteRedirectDecodeFromSiteCode();
    const info = `${siteCode}@${page}${extra ? `@${extra}` : ''}`;
    const url = new URL(originUrl);
    const route = getRouteMetadata(location.pathname);
    url.searchParams.set('site_jump_from', encodeURIComponent(info));
    url.searchParams.set('referrer_url_path', encodeURIComponent(location.href));
    url.searchParams.set(
      'referrer_page_name',
      encodeURIComponent(route?.cnName || route?.name || ''),
    );
    url.searchParams.set('referrer_path_name', encodeURIComponent(location.pathname));
    return url.toString();
  } catch {
    return originUrl;
  }
};
